import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import AdminLayout from '../../components/AdminLayout';

const DashboardContainer = styled.div`
  padding: 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => props.color};
`;

const StatTitle = styled.h3`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
`;

const StatDescription = styled.p`
  font-size: 0.75rem;
  color: #999;
`;

const StatusSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: #333;
  margin-bottom: 1rem;
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: ${props => props.status === 'online' ? '#f8f9fa' : '#fff3cd'};
  border-radius: 4px;
  border-left: 3px solid ${props => props.status === 'online' ? '#28a745' : '#ffc107'};
`;

const StatusIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.status === 'online' ? '#28a745' : '#ffc107'};
`;

const StatusText = styled.div`
  flex: 1;
`;

const StatusLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #333;
`;

const StatusValue = styled.div`
  font-size: 0.75rem;
  color: #666;
`;

const QuickActions = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const ActionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  gap: 0.75rem;

  &:hover {
    background-color: #e9ecef;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ActionIcon = styled.div`
  font-size: 1.5rem;
  color: #007bff;
`;

const ActionText = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #333;
`;

const DashboardPage = () => {
  const [stats, setStats] = useState({
    totalCustomers: 0,
    activeConversations: 0,
    aiResponses: 0,
    humanResponses: 0,
    todayConversations: 0,
    aiPercentage: 0,
    humanPercentage: 0
  });
  const [status, setStatus] = useState([
    { service: 'AI服务', status: 'online', details: '24小时在线' },
    { service: '人工服务', status: 'online', details: '9:00-18:00' },
    { service: '微信企业号', status: 'online', details: '已连接' },
    { service: 'DeepSeek API', status: 'online', details: '已配置' }
  ]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 获取仪表盘数据
  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      if (result.success) {
        setStats({
          totalCustomers: result.data.totalCustomers || 0,
          activeConversations: result.data.activeConversations || 0,
          aiResponses: result.data.aiResponses || 0,
          humanResponses: result.data.humanResponses || 0,
          todayConversations: result.data.todayConversations || 0,
          aiPercentage: result.data.aiPercentage || 0,
          humanPercentage: result.data.humanPercentage || 0
        });
        if (result.data.status) {
          setStatus(result.data.status);
        }
      }
    } catch (error) {
      console.error('获取仪表盘数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 页面加载时获取数据，并设置定时刷新
  useEffect(() => {
    fetchDashboardData();
    // 每30秒刷新一次数据
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <AdminLayout title="仪表盘">
      <DashboardContainer>
        <StatsGrid>
          <StatCard color="#007bff">
            <StatTitle>总客户数</StatTitle>
            <StatValue>{loading ? '-' : stats.totalCustomers.toLocaleString()}</StatValue>
            <StatDescription>系统注册用户总数</StatDescription>
          </StatCard>
          <StatCard color="#28a745">
            <StatTitle>活跃会话</StatTitle>
            <StatValue>{loading ? '-' : stats.activeConversations}</StatValue>
            <StatDescription>当前在线会话数</StatDescription>
          </StatCard>
          <StatCard color="#ffc107">
            <StatTitle>AI回复</StatTitle>
            <StatValue>{loading ? '-' : stats.aiResponses.toLocaleString()}</StatValue>
            <StatDescription>占比 {loading ? '-' : stats.aiPercentage}%</StatDescription>
          </StatCard>
          <StatCard color="#dc3545">
            <StatTitle>人工回复</StatTitle>
            <StatValue>{loading ? '-' : stats.humanResponses.toLocaleString()}</StatValue>
            <StatDescription>占比 {loading ? '-' : stats.humanPercentage}%</StatDescription>
          </StatCard>
        </StatsGrid>

      <StatusSection>
        <SectionTitle>服务状态</SectionTitle>
        <StatusGrid>
          {status.map((item, index) => (
            <StatusItem key={index} status={item.status}>
              <StatusIndicator status={item.status} />
              <StatusText>
                <StatusLabel>{item.service}</StatusLabel>
                <StatusValue>{item.details}</StatusValue>
              </StatusText>
            </StatusItem>
          ))}
        </StatusGrid>
      </StatusSection>

      <QuickActions>
        <SectionTitle>快速操作</SectionTitle>
        <ActionsGrid>
          <ActionButton onClick={() => handleNavigate('/admin/settings')}>
            <ActionIcon>⚙️</ActionIcon>
            <ActionText>配置管理</ActionText>
          </ActionButton>
          <ActionButton onClick={() => handleNavigate('/admin/history')}>
            <ActionIcon>💬</ActionIcon>
            <ActionText>客服记录</ActionText>
          </ActionButton>
          <ActionButton onClick={() => handleNavigate('/admin/documents')}>
            <ActionIcon>📚</ActionIcon>
            <ActionText>文档管理</ActionText>
          </ActionButton>
          <ActionButton onClick={() => handleNavigate('/admin/demands')}>
            <ActionIcon>📝</ActionIcon>
            <ActionText>需求管理</ActionText>
          </ActionButton>
          <ActionButton onClick={() => handleNavigate('/admin/announcements')}>
            <ActionIcon>📢</ActionIcon>
            <ActionText>公告管理</ActionText>
          </ActionButton>
          <ActionButton onClick={() => handleNavigate('/admin/knowledge')}>
            <ActionIcon>🧠</ActionIcon>
            <ActionText>知识库</ActionText>
          </ActionButton>
          <ActionButton onClick={() => handleNavigate('/admin/products')}>
            <ActionIcon>📦</ActionIcon>
            <ActionText>产品管理</ActionText>
          </ActionButton>
          <ActionButton onClick={() => handleNavigate('/admin/solutions')}>
            <ActionIcon>💡</ActionIcon>
            <ActionText>解决方案</ActionText>
          </ActionButton>
          <ActionButton onClick={() => handleNavigate('/admin/users')}>
            <ActionIcon>👥</ActionIcon>
            <ActionText>用户管理</ActionText>
          </ActionButton>
        </ActionsGrid>
      </QuickActions>
    </DashboardContainer>
    </AdminLayout>
  );
};

export default DashboardPage;